import React, { useCallback, useEffect, useState } from "react";

import { Button, Divider, Header } from "semantic-ui-react";

import { useIntl } from "react-intl";

import DashboardController from "react-lib/apps/IsHealth/Dashboard/DashboardController";
import ModInfoOA from "react-lib/apps/IsHealth/REG/ModInfoOA";
import UserProfileImage from "react-lib/apps/IsHealth/REG/UserProfileImage";

import PRXManager from "react-lib/apis/manager/PRXManager";

import SettingOAAccountFormBasic from "./SettingOAAccountFormBasic";
import SettingOAAccountFormDetailed from "./SettingOAAccountFormDetailed";
import SettingGoogleCalendar from "./SettingGoogleCalendar";

import CONFIG from "config/config";

type SettingOAAccountProps = {
  onEvent?: (e: any) => void;
  controller: DashboardController & {
    prxManager: PRXManager;
  };
  apiToken: string;
  division: number;
  layout?: "basic" | "detailed";
  masterData?: any;
  masterOptions?: any;
  onUpdated?: () => void;
  languageUX?: "en" | "th";
};

const HOSPITAL_IMAGE_URL = `${process.env.PUBLIC_URL}/static/images/hospital.png`;

const SettingOAAccount: React.FC<SettingOAAccountProps> = (props) => {
  const intl = useIntl();
  const [profileImageSrc, setProfileImageSrc] = useState(HOSPITAL_IMAGE_URL);
  const [divisionProfile, setDivisionProfile] = useState<Record<string, any>>({});
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [fileLogoImage, setFileLogoImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [healthFacilityTypeOptions, setHealthFacilityTypeOptions] = useState<Record<string, any>[]>(
    [],
  );
  const [modInfoMsg, setModInfoMsg] = useState("");
  const [error, setError] = useState<Record<string, any>>({});
  const [apiErrors, setApiErrors] = useState<Record<string, string[]>>({});

  const getHealthFacilityType = useCallback(async () => {
    if (!props.controller.getHealthFacilityType) {
      return;
    }

    const [types]: [Record<string, any>[]] = await props.controller.getHealthFacilityType({
      apiToken: props.apiToken,
    });

    const options = types.map((item: any) => ({
      key: item.name,
      text: item.name,
      value: item.name,
    }));

    setHealthFacilityTypeOptions(options);
  }, [props.apiToken]);

  // Fetch division profile
  const getOADivisionProfile = useCallback(async () => {
    if (!props.division || !props.apiToken) {
      return;
    }

    setIsLoading(true);

    try {
      const [res] = await props.controller.prxManager.getOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
      });

      if (res) {
        const accountingProvince = (props.masterData?.province || [])?.find(
          (p: any) => p.id === res.accounting_province,
        );

        const accountingDistrict = (props.masterData?.district || [])?.find(
          (d: any) => d.id === res.accounting_district,
        );

        const accountingCity = (props.masterData?.city || [])?.find(
          (c: any) => c.id === res.accounting_city,
        );

        setDivisionProfile({
          ...res,
          account_link: res.token
            ? `${CONFIG.OFFICIAL_ACCOUNT_HOST || "https://ishealth.app"}/OA/${
                res.id
              }/?openExternalBrowser=1`
            : "",
          business: {
            branch_name: res.branch_name,
            branch_type: res.branch_type || "",
            business_name: res.business_name,
            business_type: res.business_type,
            first_name: res.first_name,
            last_name: res.last_name,
            tax_payer_id: res.tax_payer_id,
          },
          division_address: {
            address_text: res.address_text,
            city: res.city?.id,
            district: res.district?.id,
            latitude: res.latitude,
            longitude: res.longitude,
            place_id: res.place_id,
            province: res.province?.id,
            zipcode: res.city?.zipcode,
          },
          accounting: {
            accounting_business_name: res.accounting_business_name,
            accounting_tax_payer_id: res.accounting_tax_payer_id,
            accounting_address_text: res.accounting_address_text,
            accounting_city: accountingCity?.id,
            accounting_district: accountingDistrict?.id,
            accounting_province: accountingProvince?.id,
            accounting_zipcode: accountingCity?.zipcode,
            accounting_image_file: res.accounting_image_file,
          },
        });

        setProfileImageSrc(res.image_file || HOSPITAL_IMAGE_URL);
      }
    } catch (error) {
      console.error("Error fetching division profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    props.apiToken,
    props.division,
    props.masterData?.province,
    props.masterData?.district,
    props.masterData?.city,
  ]);

  // Handle profile update
  const handleEditProfile = async () => {
    if (!divisionProfile) {
      return;
    }

    setIsLoading(true);
    setApiErrors({});

    try {
      const requiredAddressFields = ["address_text", "province", "district", "city"];
      const requiredBusinessFields = ["business_type", "tax_payer_id"];
      const requiredAccountingFields = [
        "accounting_business_name",
        "accounting_address_text",
        "accounting_tax_payer_id",
        "accounting_address_text",
        "accounting_province",
        "accounting_district",
        "accounting_city",
      ];

      if (divisionProfile.business?.business_type === "INDIVIDUAL") {
        requiredBusinessFields.push("first_name", "last_name");

        if (divisionProfile.business?.branch_type !== "") {
          requiredBusinessFields.push("branch_type");
        }

        if (divisionProfile.business.branch_type === "BRANCH") {
          requiredBusinessFields.push("branch_name");
        }
      } else if (divisionProfile.business?.business_type === "LEGAL_ENTITY") {
        requiredBusinessFields.push("business_name", "branch_type");
      }

      const missingAddressFields = requiredAddressFields.filter(
        (field) => !divisionProfile.division_address?.[field],
      );
      const missingBusinessFields = requiredBusinessFields.filter(
        (field) =>
          divisionProfile.business?.[field] == null || divisionProfile.business?.[field] === "",
      );
      const missingAccountingFields = requiredAccountingFields.filter(
        (field) =>
          divisionProfile.accounting?.[field] == null || divisionProfile.accounting?.[field] === "",
      );
      const errors = {
        ...Object.fromEntries(missingAddressFields.map((f) => [f, true])),
        ...Object.fromEntries(missingBusinessFields.map((f) => [f, true])),
        ...Object.fromEntries(missingAccountingFields.map((f) => [f, true])),
        ...(!divisionProfile.hospital_name && { hospital_name: true }),
      };

      const taxid = divisionProfile.business?.tax_payer_id || "";
      const compact = taxid.replaceAll(" ", "");

      if (compact.length !== 13 || !/^\d{13}$/.test(compact)) {
        errors.tax_payer_id = true;
      }

      const accountingTaxid = divisionProfile.accounting?.accounting_tax_payer_id || "";
      const accountingCompact = accountingTaxid.replaceAll(" ", "");

      if (accountingCompact.length !== 13 || !/^\d{13}$/.test(accountingCompact)) {
        errors.accounting_tax_payer_id = true;
      }

      if (!fileLogoImage && !divisionProfile.accounting?.accounting_image_file) {
        errors.accounting_image_file = true;
      }

      if (Object.keys(errors).length > 0) {
        setError(errors);

        return;
      }

      setError({});

      const [, error] = await props.controller.prxManager.patchOADivisionProfile({
        apiToken: props.apiToken,
        divisionId: props.division,
        ...divisionProfile,
        addressText: divisionProfile.division_address?.address_text,
        divisionName: divisionProfile.name,
        hasStaff: divisionProfile.has_staff,
        hospitalName: divisionProfile.hospital_name,
        imageFile: fileImage,
        oaType: divisionProfile.oa_type,
        placeId: divisionProfile.division_address?.place_id,
        premiumUrl: divisionProfile.premium_url,
        systemName: divisionProfile.system_name,
        ...divisionProfile.division_address,
        branchName: divisionProfile.business?.branch_name,
        branchType: divisionProfile.business?.branch_type,
        businessName: divisionProfile.business?.business_name,
        businessType: divisionProfile.business?.business_type,
        firstName: divisionProfile.business?.first_name,
        lastName: divisionProfile.business?.last_name,
        taxPayerId: divisionProfile.business?.tax_payer_id,
        accountingAddressText: divisionProfile.accounting?.accounting_address_text,
        accountingBusinessName: divisionProfile.accounting?.accounting_business_name,
        accountingCity: divisionProfile.accounting?.accounting_city,
        accountingDistrict: divisionProfile.accounting?.accounting_district,
        accountingProvince: divisionProfile.accounting?.accounting_province,
        accountingTaxPayerId: divisionProfile.accounting?.accounting_tax_payer_id,
        accountingImageFile: fileLogoImage,
      });

      if (error) {
        if (props.layout === "detailed") {
          setModInfoMsg("เกิดข้อผิดพลาด");
          setApiErrors(error || {});
        }

        console.error("Error editing profile:", error);

        return;
      }

      console.log("Profile updated successfully");

      // Refresh profile data
      await getOADivisionProfile();

      props.onUpdated?.();

      if (props.layout === "detailed") {
        setModInfoMsg("บันทึกสำเร็จ");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeValue = (e: any, data: any) => {
    console.log("handleChangeValue data:", data);
    const name: string = data.name || "";
    const value = data.type === "checkbox" ? data.checked : data.value;

    const [first, second] = name.split(".");

    setDivisionProfile((prev) => ({
      ...prev,
      [first]: second
        ? {
            ...(prev as any)[first],
            [second]: value,
          }
        : value,
    }));
  };

  // Handle logo image change
  const handleLogoImageChange = useCallback((file: File, fileUrl: string) => {
    setFileLogoImage(file);
  }, []);

  // Handle image change from UserProfileImage component
  const handleImageChange = useCallback((file: File, fileUrl: string) => {
    setFileImage(file);
    setProfileImageSrc(fileUrl);
  }, []);

  // Handle image clear from UserProfileImage component
  const handleClearImage = useCallback(() => {
    setFileImage(null);
    setProfileImageSrc(divisionProfile.image_file || HOSPITAL_IMAGE_URL);
  }, [divisionProfile.image_file]);

  // Load profile on mount and when division changes
  useEffect(() => {
    void getOADivisionProfile();
  }, [getOADivisionProfile]);

  useEffect(() => {
    void getHealthFacilityType();

    props.onEvent?.({
      message: "GetMasterData",
      params: {
        masters: [
          ["province", {}],
          ["district", {}],
          ["city", {}],
        ],
      },
    });
  }, []);

  return (
    <div style={{ height: "calc(100vh - 40px)", overflowY: "auto", padding: "20px" }}>
      <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
        <Header style={{ color: "#4f4f4f", paddingTop: "15px" }} as="h2">
          {intl.formatMessage({ id: "ตั้งค่าบัญชี OA" })}
        </Header>
        <p
          style={{
            alignItems: "center",
            backgroundColor: "#d9d9d9",
            borderRadius: "4px",
            padding: "10px",
          }}
        >
          Official Account ID: {divisionProfile.official_account_id}
        </p>
      </div>
      <Divider style={{ marginBottom: "25px", marginTop: 0 }} />

      <div className="register-new-user" style={{ height: "auto" }}>
        <UserProfileImage
          src={profileImageSrc}
          onChange={handleImageChange}
          onClear={handleClearImage}
        />

        <div className="vl" />

        {props.layout === "detailed" ? (
          <div className="profile-info" style={{ borderLeft: 0, marginBottom: "3rem" }}>
            <SettingOAAccountFormDetailed
              data={divisionProfile}
              error={error}
              masterData={props.masterData}
              masterOptions={props.masterOptions}
              healthFacilityTypeOptions={healthFacilityTypeOptions}
              onChangeValue={handleChangeValue}
              onChangeLogoImage={handleLogoImageChange}
              languageUX={props.languageUX}
            />

            <br />

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
                disabled={isLoading}
                loading={isLoading}
                type="submit"
                onClick={handleEditProfile}
              >
                บันทึกข้อมูลการแก้ไข
              </Button>
            </div>
          </div>
        ) : (
          <SettingOAAccountFormBasic
            data={divisionProfile}
            isLoading={isLoading}
            onChangeValue={handleChangeValue}
            onSubmit={handleEditProfile}
          />
        )}
      </div>

      {/* Google Calendar ของแผนกนี้ (sync นัดหมายขึ้นปฏิทิน) */}
      <Divider style={{ marginBottom: "25px" }} />
      <div style={{ maxWidth: "900px" }}>
        <SettingGoogleCalendar
          apiToken={props.apiToken}
          division={props.division}
          divisionName={divisionProfile?.name}
        />
      </div>

      <ModInfoOA
        apiErrors={apiErrors}
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={() => {
          setModInfoMsg("");
        }}
        onClose={() => {
          setModInfoMsg("");
        }}
      />
    </div>
  );
};

export default SettingOAAccount;
